import { nanoid } from 'nanoid';

const attemptTtlMs = 24 * 60 * 60 * 1000;
const storagePrefix = 'fc-admin.action-attempt.v1.';
const storageScanLimit = 32;
const validKey = /^[A-Za-z0-9:._-]{8,128}$/;

const canonicalize = (value: unknown): string => {
    if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
    if (Array.isArray(value)) return `[${value.map(canonicalize).join(',')}]`;
    const object = value as Record<string, unknown>;
    return `{${Object.keys(object).sort().map((key) => `${JSON.stringify(key)}:${canonicalize(object[key])}`).join(',')}}`;
};

const hex = (value: ArrayBuffer) => Array.from(new Uint8Array(value))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');

const digest = async (algorithm: 'SHA-256' | 'SHA-384', value: string) => {
    try {
        const subtle = globalThis.crypto?.subtle;
        if (!subtle) return undefined;
        return hex(await subtle.digest(algorithm, new TextEncoder().encode(value)));
    } catch {
        return undefined;
    }
};

type AttemptStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
    & Partial<Pick<Storage, 'key' | 'length'>>;

type AttemptLockManager = {
    request: <T>(name: string, callback: () => Promise<T>) => Promise<T>;
};

type AttemptRecord = {
    key: string;
    expiresAt: number;
    storageKey?: string;
    verifier?: string;
};

type StoredAttempt = {
    version: 1;
    key: string;
    expiresAt: number;
    verifier: string;
};

export type NativeActionAttempt = {
    fingerprint: string;
    key: string;
    storageKey?: string;
    verifier?: string;
};

const browserStorage = () => {
    try {
        return typeof window === 'undefined' ? undefined : window.localStorage;
    } catch {
        return undefined;
    }
};

const browserLocks = (): AttemptLockManager | undefined => {
    try {
        return typeof navigator === 'undefined' || !navigator.locks
            ? undefined
            : navigator.locks as AttemptLockManager;
    } catch {
        return undefined;
    }
};

export class NativeActionAttemptStore {
    private readonly attempts = new Map<string, AttemptRecord>();
    private readonly pending = new Map<string, Promise<NativeActionAttempt>>();
    private readonly keyOwners = new Map<string, string>();
    private storageUnavailable = false;
    private storageScanCursor = 0;
    private sequence = 0;

    constructor(
        private readonly createId: () => string = nanoid,
        private readonly storage: AttemptStorage | undefined = browserStorage(),
        private readonly now: () => number = Date.now,
        private readonly locks: AttemptLockManager | undefined = browserLocks(),
    ) {
        this.pruneStorage();
    }

    async begin(
        actorId: string,
        scope: string,
        request: unknown,
        proposedKey?: string,
    ): Promise<NativeActionAttempt> {
        this.pruneStorage();
        const fingerprint = canonicalize({ actorId, scope, request });
        const current = this.getMemoryAttempt(fingerprint);
        if (current) return current;
        const pending = this.pending.get(fingerprint);
        if (pending) return pending;
        const promise = this.beginNew(fingerprint, scope, proposedKey);
        this.pending.set(fingerprint, promise);
        try {
            return await promise;
        } finally {
            if (this.pending.get(fingerprint) === promise) this.pending.delete(fingerprint);
        }
    }

    complete(attempt: NativeActionAttempt) {
        const current = this.attempts.get(attempt.fingerprint);
        if (!current || current.key !== attempt.key) return;
        this.attempts.delete(attempt.fingerprint);
        if (this.keyOwners.get(attempt.key) === attempt.fingerprint) this.keyOwners.delete(attempt.key);
        if (!attempt.storageKey || !attempt.verifier) return;
        const stored = this.readStored(attempt.storageKey);
        if (stored?.key === attempt.key && stored.verifier === attempt.verifier) {
            this.removeStored(attempt.storageKey);
        }
    }

    private getMemoryAttempt(fingerprint: string) {
        const current = this.attempts.get(fingerprint);
        if (!current) return undefined;
        if (current.expiresAt <= this.now()) {
            this.attempts.delete(fingerprint);
            if (this.keyOwners.get(current.key) === fingerprint) this.keyOwners.delete(current.key);
            if (current.storageKey) this.removeStored(current.storageKey);
            return undefined;
        }
        return this.publicAttempt(fingerprint, current);
    }

    private async beginNew(fingerprint: string, scope: string, proposedKey?: string) {
        const [fingerprintDigest, verifier] = await Promise.all([
            digest('SHA-256', fingerprint),
            digest('SHA-384', fingerprint),
        ]);
        const create = () => this.beginWithDigests(
            fingerprint,
            scope,
            fingerprintDigest,
            verifier,
            proposedKey,
        );
        if (!fingerprintDigest || !this.locks) return create();
        try {
            return await this.locks.request(`${storagePrefix}lock.${fingerprintDigest}`, create);
        } catch {
            return create();
        }
    }

    private async beginWithDigests(
        fingerprint: string,
        scope: string,
        fingerprintDigest: string | undefined,
        verifier: string | undefined,
        proposedKey?: string,
    ) {
        const storageKey = fingerprintDigest && verifier
            ? `${storagePrefix}${fingerprintDigest}`
            : undefined;
        let storageCollision = false;
        if (storageKey && verifier) {
            const stored = this.readStored(storageKey);
            if (stored && stored.verifier === verifier && !this.hasKeyCollision(stored.key, fingerprint)) {
                const record = { ...stored, storageKey };
                this.remember(fingerprint, record);
                return this.publicAttempt(fingerprint, record);
            }
            storageCollision = stored !== undefined && stored.verifier !== verifier;
        }

        const collisionSalt = storageCollision ? this.createId() : '';
        const record: AttemptRecord = {
            key: await this.createKey(scope, fingerprintDigest, verifier, proposedKey, collisionSalt),
            expiresAt: this.now() + attemptTtlMs,
            storageKey: storageCollision ? undefined : storageKey,
            verifier: storageCollision ? undefined : verifier,
        };
        this.remember(fingerprint, record);
        if (record.storageKey && record.verifier) {
            const stored = this.writeStored(record.storageKey, {
                version: 1,
                key: record.key,
                expiresAt: record.expiresAt,
                verifier: record.verifier,
            });
            if (stored && stored.verifier === record.verifier && !this.hasKeyCollision(stored.key, fingerprint)) {
                record.key = stored.key;
                record.expiresAt = stored.expiresAt;
                this.remember(fingerprint, record);
            } else if (stored) {
                record.storageKey = undefined;
                record.verifier = undefined;
            }
        }
        return this.publicAttempt(fingerprint, record);
    }

    private async createKey(
        scope: string,
        fingerprintDigest: string | undefined,
        verifier: string | undefined,
        proposedKey?: string,
        collisionSalt = '',
    ) {
        const cleanScope = scope.replace(/[^A-Za-z0-9._-]/g, '_').slice(0, 32) || 'action';
        const fingerprintKey = fingerprintDigest && verifier
            ? `${fingerprintDigest.slice(0, 12)}${verifier.slice(0, 12)}`
            : fingerprintDigest?.slice(0, 20);
        for (let attempt = 0; attempt < 8; attempt += 1) {
            const sequence = ++this.sequence;
            const seed = `${proposedKey ?? this.createId()}\0${collisionSalt}\0${this.now()}\0${sequence}`;
            const entropy = await digest('SHA-256', seed);
            const candidate = fingerprintKey && entropy
                ? `fc:${cleanScope}:${fingerprintKey}:${entropy.slice(0, 20)}`
                : `fc:${cleanScope}:${String(this.createId()).replace(/[^A-Za-z0-9._-]/g, '_').slice(0, 32)}:${sequence.toString(36)}`;
            if (validKey.test(candidate) && !this.keyOwners.has(candidate)) return candidate;
        }
        return `fc:action:${nanoid(32)}`;
    }

    private remember(fingerprint: string, record: AttemptRecord) {
        const previous = this.attempts.get(fingerprint);
        if (previous && this.keyOwners.get(previous.key) === fingerprint) this.keyOwners.delete(previous.key);
        this.attempts.set(fingerprint, record);
        this.keyOwners.set(record.key, fingerprint);
    }

    private hasKeyCollision(key: string, fingerprint: string) {
        const owner = this.keyOwners.get(key);
        return owner !== undefined && owner !== fingerprint;
    }

    private publicAttempt(fingerprint: string, record: AttemptRecord): NativeActionAttempt {
        return {
            fingerprint,
            key: record.key,
            storageKey: record.storageKey,
            verifier: record.verifier,
        };
    }

    private pruneStorage() {
        if (!this.storage || this.storageUnavailable || typeof this.storage.length !== 'number'
            || typeof this.storage.key !== 'function') return;
        try {
            const length = this.storage.length;
            if (!Number.isSafeInteger(length) || length <= 0) {
                this.storageScanCursor = 0;
                return;
            }
            const count = Math.min(length, storageScanLimit);
            const keys: string[] = [];
            for (let offset = 0; offset < count; offset += 1) {
                const key = this.storage.key((this.storageScanCursor + offset) % length);
                if (key?.startsWith(storagePrefix)) keys.push(key);
            }
            this.storageScanCursor = (this.storageScanCursor + count) % length;
            for (const key of keys) this.readStored(key);
        } catch {
            this.storageUnavailable = true;
        }
    }

    private readStored(storageKey: string): StoredAttempt | undefined {
        if (!this.storage || this.storageUnavailable) return undefined;
        try {
            const raw = this.storage.getItem(storageKey);
            if (!raw) return undefined;
            const value = JSON.parse(raw) as Partial<StoredAttempt>;
            const now = this.now();
            if (value.version !== 1 || typeof value.key !== 'string' || !validKey.test(value.key)
                || typeof value.expiresAt !== 'number' || value.expiresAt <= now
                || value.expiresAt > now + attemptTtlMs || typeof value.verifier !== 'string'
                || !/^[a-f0-9]{96}$/.test(value.verifier)) {
                this.storage.removeItem(storageKey);
                return undefined;
            }
            return value as StoredAttempt;
        } catch {
            this.storageUnavailable = true;
            return undefined;
        }
    }

    private writeStored(storageKey: string, record: StoredAttempt) {
        if (!this.storage || this.storageUnavailable) return undefined;
        try {
            this.storage.setItem(storageKey, JSON.stringify(record));
        } catch {
            this.storageUnavailable = true;
            return undefined;
        }
        return this.readStored(storageKey);
    }

    private removeStored(storageKey: string) {
        if (!this.storage || this.storageUnavailable) return;
        try {
            this.storage.removeItem(storageKey);
        } catch {
            this.storageUnavailable = true;
        }
    }
}

export const nativeActionAttemptStore = new NativeActionAttemptStore();
